/**
 * Client-side CSV header preview for the "New import" wizard (S-09-08): the admin must assign
 * column roles (`options.csvColumns`) *before* the batch is created (there is no endpoint to
 * patch it afterward — `POST /imports` is the only place `options` lands), so the wizard reads
 * just the header line itself, well before `CsvAdapter.discover()` ever runs server-side. This
 * is a light echo of `packages/jobs/src/imports/csv/parse.ts`'s delimiter detection (count
 * candidate delimiters in the header line, pick the most common) — good enough for a role
 * picker; the server's own parse is still the authority for the actual import.
 */

const CANDIDATE_DELIMITERS = [',', ';', '\t'] as const;

function detectDelimiter(headerLine: string): string {
  let best: { delimiter: string; count: number } = { delimiter: ',', count: -1 };
  for (const delimiter of CANDIDATE_DELIMITERS) {
    const count = headerLine.split(delimiter).length - 1;
    if (count > best.count) {
      best = { delimiter, count };
    }
  }
  return best.delimiter;
}

const unquote = (cell: string): string => {
  const trimmed = cell.trim();
  const match = /^"(.*)"$/.exec(trimmed);
  return match ? (match[1] as string).replace(/""/g, '"') : trimmed;
};

/** Parses just the header row of CSV text into column names (BOM-stripped, quotes unwrapped). */
export function parseCsvHeaderLine(text: string): string[] {
  const withoutBom = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const firstLine = withoutBom.split(/\r\n|\n|\r/, 1)[0] ?? '';
  if (firstLine.trim().length === 0) {
    return [];
  }
  const delimiter = detectDelimiter(firstLine);
  return firstLine.split(delimiter).map(unquote);
}
